import React, {Fragment, useEffect, useState} from 'react';
import {consultantDictionary, ControlConsultantComponent} from '../../../controls/agyle/clinitian';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import {user} from '../../../store/reducers/user';
import moment from 'moment';
import {calculateArrivalDate} from '../../../util/patientUtil';
import Grid from '@mui/material/Grid';
import {ControlInvestigations, ControlRapidAssessment, ControlTreatment} from '../../../controls/agyle/diagnosis';
import makeStyles from '@mui/styles/makeStyles';
import _ from 'lodash';
import keyPerformanceIndicator from '../../../util/timeMarker';
import Notes from '../../../util/notes';
import Formatter from '../../../util/formatter';
import MessageBox from '../../../util/messageBox';
import {patientBanner} from '../../../controls/agyle/tasks/components/patientBanner';
import AgyleButton from '../../../components/ui/buttons/agyleButton';
import AgyleDatePicker from '../../../components/ui/fields/agyleDatePicker';
import NewNoteModal from '../../../components/notes/component/newNoteModal';
import useOpenState from '../../../hooks/useOpenState';
import {triageBase} from './triage/triageUtils';
import MiniFormDialog from '../../../components/modal/miniFormDialog';
import AgyleTextField from '../../../components/ui/fields/agyleTextField';
import {getConfig} from '../../../util/configUtil';

const useStyles = makeStyles((theme) => ({
    background: {
        backgroundColor: theme.palette.background.paperDarker,
    },
    dialogTitle: {
        textAlign: 'center',
        padding: '10px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingLeft: '24px',
        paddingRight: '24px',
    },

    closeButton: {
        width: '30px',
        height: '30px',
        paddingTop: '13px',
        paddingRight: '10px',
    },
    dialogContent: {
        height: 'auto',
    },
    error: {
        color: '#f44336',
    },
    paper: {
        background: theme.palette.background.paperDarker,
        padding: 10,
        marginBottom: 10,
    },
    margin: {
        marginTop: 5,
    },
}));

const RapidAssessment = (props) => {
    const {t} = useTranslation();
    const classesLocal = useStyles();
    const userObject = useSelector(user);
    const tester = Formatter.formatUserFullName(userObject);

    const controller = new AbortController();

    const [seen, setSeen] = useState({
        who: null,
        when: moment().toISOString(),
        timestamp: null,
        investigations: null,
        treatments: null,
        additionalInformation: '',
        rapidAssessment: null,
    });
    const [org, setOrg] = useState(null);
    const [isError, setIsError] = useState(false);
    const [errorPicker, setErrorPicker] = useState(null);
    const [formIsModified, setFormIsModified] = React.useState(props.isActionMode);

    const [fullFormMode, setFullFormMode] = React.useState(!props.isActionMode);

    const [loading, setLoading] = useState(true);

    const [openConfirmDeleted, setOpenConfirmDeleted] = React.useState(false);
    const [isOpenNoteOpen, openOpenNote, closeOpenNote] = useOpenState(false, true);

    // Configs
    const defaultDateTimeFormat = getConfig('defaultDateTimeFormat');
    const clinicalNotesFromForm = getConfig('clinicalNotesFromForm', false);
    const showRapidAssessmentDictionaryDropdown = getConfig('showRapidAssessmentDictionaryDropdown', false);

    // Init data
    useEffect(() => {
        if (props.patient?.seen) {
            setSeen(prevState => ({
                ...prevState,
                ...props.patient?.seen,
                when: props.patient?.seen?.when ? props.patient?.seen?.when : moment().toISOString(),
            }));
            setOrg({...props.patient?.seen});

            if (props.patient.seen.who && props.patient.seen.when && props.patient.seen.status === 'timestamp')
                setFormIsModified(true);
        }

        setLoading(false);
        return () => {
            controller.abort();
        };
    }, []);

    const checkDifferent = (path, isDate) => {
        const newVal = _.get(seen, path);
        const orgVal = _.get(org, path);

        if (orgVal && orgVal !== newVal) {
            return `changed from ${isDate ? moment(orgVal).format(defaultDateTimeFormat) : orgVal} ` +
                `to ${isDate ? moment(newVal).format(defaultDateTimeFormat) : newVal}`;
        }

        return isDate ? moment(newVal).format(defaultDateTimeFormat) : newVal;
    };

    const prepareNote = (isClear, fullFormMode) => {
        return fullFormMode ?
            Notes.getActionRapidAssessmentNote(checkDifferent('who.__description', false), checkDifferent('when', true))
            :
            Notes.getRapidAssessment(
                seen,
                isClear,
                tester,
                checkDifferent('who.__description', false),
                checkDifferent('when', true),
            );
    };

    const handleClear = () => {
        const data = ([{
            name: 'seen',
            value: {
                who: null,
                when: null,
                timestamp: moment().toISOString(),
                investigations: null,
                treatments: null,
                additionalInformation: '',
                by: tester,
                byLogin: userObject?.userName,
                status: 'new',
            },
            noteToAdd: prepareNote(true),
        }]);

        setOpenConfirmDeleted(false);
        props.onChange(data);
        props.onClose();
    };

    const valid = () => {
        setIsError(!seen?.who || errorPicker);
        return seen?.who && seen?.when && !errorPicker;
    };

    const sendChange = () => {
        if (valid()) {
            const objToSave = ([{
                name: 'seen',
                value: {
                    ...seen,
                    timestamp: moment().toISOString(),
                    by: tester,
                    byLogin: userObject?.userName,
                    status: fullFormMode ? 'completed' : 'timestamp',
                },
                noteToAdd: prepareNote(false, !fullFormMode),
            }]);

            let clone = _.cloneDeep(props.patient?.triage);
            if (!clone) {
                clone = triageBase();
            }
            clone.treatmentAndManagementPlan = _.unionBy(clone.treatmentAndManagementPlan, seen.treatments, 'value.snomedCode');
            clone.investigations = _.unionBy(clone.investigations, seen.investigations, 'value.snomedCode');

            objToSave.push({
                name: 'triage',
                value: clone,
            });

            keyPerformanceIndicator(
                objToSave,
                props.patient,
                ['firstInitialAssessment', 'firstClinicianSaved'],
                {
                    when: seen.when,
                    who: seen.who,
                },
            );

            props.onChange(objToSave);
            props.onClose();
        }
    };

    const getAdditionalButtons = () => {
        const buttons = [];

        if (!props.disableSave) {
            if (clinicalNotesFromForm) {
                buttons.push(
                    <AgyleButton
                        variant="outlined"
                        onClick={openOpenNote}
                        key="add-note"
                    >
                        {t('Add note')}
                    </AgyleButton>,
                );
            }
            buttons.push(
                <AgyleButton
                    variant="outlined"
                    onClick={() => setOpenConfirmDeleted(true)}
                    key="delete"
                >
                    {t('Delete')}
                </AgyleButton>,
            );
        }
        return buttons;
    };

    return (
        <>
            <MiniFormDialog
                title={t(props._formTitle)}
                open={props.open}
                onClose={props.onClose}
                disableSubmit={!formIsModified}
                onSave={sendChange}
                buttons={getAdditionalButtons()}
            >
                <Grid container spacing={2}>
                    {!fullFormMode &&
                        <Grid item xs={12}>
                            {patientBanner(props.patient, true)}
                        </Grid>
                    }

                    <Grid item xs={12}>
                        <ControlConsultantComponent
                            allowedConsultants={consultantDictionary.rapidAssessment}
                            findUser={userObject?.userName}
                            loading={loading}
                            error={isError && !seen.who}
                            label={t('Rapid Assessment Clinician')}
                            value={seen.who}
                            agyleRefresh
                            onChange={value => {
                                setSeen(prevState => ({
                                    ...prevState,
                                    who: value,
                                }));
                                setFormIsModified(true);
                            }}
                            noBorder={false}
                            withNewBackground
                            size="large"
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <AgyleDatePicker
                            type="dateTime"
                            label={t('Date and Time')}
                            error={isError && !seen?.when}
                            value={seen.when}
                            minDate={moment(calculateArrivalDate(props.patient))}
                            maxDate={moment()}
                            onError={setErrorPicker}
                            onChange={value => {
                                setSeen(prevState => ({
                                    ...prevState,
                                    when: moment(value).toISOString(),
                                }));
                                setFormIsModified(true);
                            }}
                            noBorder={false}
                            withNewBackground
                            size="large"
                        />
                    </Grid>
                </Grid>

                {fullFormMode &&
                    <Grid container spacing={2} className={classesLocal.margin}>
                        <Grid item xs={12} style={{width: '100%'}}>
                            <ControlInvestigations
                                agyleRefresh
                                label={t('Investigations')}
                                value={seen.investigations}
                                disableEditTable={props.disableSave}
                                disabled={props.disableSave}
                                onChange={value => {
                                    setSeen(prevState => ({
                                        ...prevState,
                                        investigations: value,
                                    }));
                                    setFormIsModified(true);
                                }}
                                disableCloseOnSelect
                                showIndicatedFlag={'forFAB'}
                                showAll
                                admitDate={props.patient?.admission?.startDate}
                                withBorder
                            />
                        </Grid>

                        <Grid item xs={12} style={{width: '100%'}}>
                            <ControlTreatment
                                label={t('Treatment')}
                                agyleRefresh
                                value={seen.treatments}
                                disableEditTable={props.disableSave}
                                disabled={props.disableSave}
                                onChange={value => {
                                    setSeen(prevState => ({
                                        ...prevState,
                                        treatments: value,
                                    }));
                                    setFormIsModified(true);
                                }}
                                disableCloseOnSelect
                                showIndicatedFlag={'forFAB'}
                                showAll
                                admitDate={props.patient?.admission?.startDate}
                                withBorder
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <AgyleTextField
                                disabled={props.disableSave}
                                label={t('Rapid Assessment Summary')}
                                multiline
                                value={seen.additionalInformation}
                                onChange={event => {
                                    setSeen(prevState => ({
                                        ...prevState,
                                        additionalInformation: event.target.value,
                                    }));
                                    setFormIsModified(true);
                                }}
                                fullWidth
                                debounce
                                noBorder={false}
                                withNewBackground
                                size="large"
                            />
                        </Grid>

                        <Grid item xs={12} style={{width: '100%'}}>
                            {showRapidAssessmentDictionaryDropdown &&
                                <ControlRapidAssessment
                                    label={t('Rapid Assessment')}
                                    value={seen.rapidAssessment}
                                    disabled={props.disableSave}
                                    onChange={value => {
                                        setSeen(prevState => ({
                                            ...prevState,
                                            rapidAssessment: value,
                                        }));
                                        setFormIsModified(true);
                                    }}
                                    showAll
                                    noBorder={false}
                                    withNewBackground
                                    size="large"
                                />
                            }
                        </Grid>
                    </Grid>
                }
            </MiniFormDialog>

            <MessageBox
                title={t('CAUTION')}
                haveAccept={{yes: t('Yes'), no: t('No')}}
                onAccept={handleClear}
                onClose={() => setOpenConfirmDeleted(false)}
                message={t('Are you sure you want to delete all the recorded values?')}
                open={openConfirmDeleted}
            />

            <NewNoteModal
                open={isOpenNoteOpen}
                onClose={closeOpenNote}
                patient={props.patient}
                disableSave={props.disableSave}
                fromForm="Rapid Assessment"
            />
        </>
    );
};

export default RapidAssessment;